import { useState } from "react";
import {
  CreateTradeSections,
  TradeFormSetters,
  TradeFormValues,
} from "@shared/features/tradingTerminal/common/types";
import { OrderTypes } from "@shared/types";
import { setStateByType } from "@shared/features/tradingTerminal/common/utils";
import {
  DEFAULT_SIGNAL_AMOUNT,
  TRADE_DEFAULT_STOP,
} from "@shared/features/tradingTerminal/common/constants";

const useTradeFormValues = (isSignal?: boolean) => {
  const [account, setAccount] = useState<TradeFormValues["account"]>();
  const [exchanges, setExchanges] = useState<TradeFormValues["exchanges"]>();
  const [subGroupIds, setSubGroupIds] =
    useState<TradeFormValues["subGroupIds"]>();
  const [accountChanged, setAccountChanged] =
    useState<TradeFormValues["accountChanged"]>(false);
  const [symbols, setSymbols] = useState<TradeFormValues["symbols"]>([]);
  const [symbolChanged, setSymbolChanged] =
    useState<TradeFormValues["symbolChanged"]>(false);

  const [amount, setAmount] = useState<TradeFormValues["amount"]>(
    isSignal ? DEFAULT_SIGNAL_AMOUNT : "0"
  );
  const [amountType, setAmountType] = useState<
    TradeFormValues["amountType"] | undefined
  >(undefined);
  const [amountPercent, setAmountPercent] =
    useState<TradeFormValues["amountPercent"]>("0");
  const [amountInitialized, setAmountInitialized] = useState<
    TradeFormValues["amountInitialized"]
  >(!!isSignal);
  const [quantity, setQuantity] = useState<TradeFormValues["quantity"]>("0");
  const [quantityPercent, setQuantityPercent] =
    useState<TradeFormValues["quantityPercent"]>("0");
  const [extraInfo, setExtraInfo] = useState<TradeFormValues["extraInfo"]>();
  const [generalDataProcessed, setGeneralDataProcessed] =
    useState<TradeFormValues["generalDataProcessed"]>(false);

  const [entryOrders, setEntryOrders] = useState<
    TradeFormValues["entryOrders"]
  >([]);
  const [takeProfitOrders, setTakeProfitOrders] = useState<
    TradeFormValues["takeProfitOrders"]
  >([]);
  const [stopOrders, setStopOrders] = useState<TradeFormValues["stopOrders"]>(
    []
  );
  const [defaultStop, setDefaultStop] =
    useState<TradeFormValues["defaultStop"]>(TRADE_DEFAULT_STOP);
  const [existingQuantityOrder, setExistingQuantityOrder] =
    useState<TradeFormValues["existingQuantityOrder"]>();
  const [useTakeProfitOrders, setUseTakeProfitOrders] =
    useState<TradeFormValues["useTakeProfitOrders"]>(true);
  const [useStopOrders, setUseStopOrders] =
    useState<TradeFormValues["useStopOrders"]>(false);

  const [trailingEntry, setTrailingEntry] =
    useState<TradeFormValues["trailingEntry"]>();
  const [trailingTakeProfit, setTrailingTakeProfit] =
    useState<TradeFormValues["trailingTakeProfit"]>();
  const [trailingStop, setTrailingStop] =
    useState<TradeFormValues["trailingStop"]>();
  const [stopTimeout, setStopTimeout] =
    useState<TradeFormValues["stopTimeout"]>();
  const [useTrailing, setUseTrailing] = useState<
    TradeFormValues["useTrailing"]
  >({
    [OrderTypes.ENTRY]: false,
    [OrderTypes.TAKE_PROFIT]: false,
    [OrderTypes.STOP]: false,
  });

  const [tabWithErrors, setTabWithErrors] = useState({
    [CreateTradeSections.GENERAL]: false,
    [CreateTradeSections.ENTRIES]: false,
    [CreateTradeSections.TAKE_PROFITS]: false,
    [CreateTradeSections.STOP]: false,
  });

  const [rawFreeText, setRawFreeText] =
    useState<TradeFormValues["rawFreeText"]>();
  const [freeTextTradeData, setFreeTextTradeData] =
    useState<TradeFormValues["freeTextTradeData"]>();
  const [freeTextProcessed, setFreeTextProcessed] =
    useState<TradeFormValues["freeTextProcessed"]>(false);
  const [useFreeTextData, setUseFreeTextData] =
    useState<TradeFormValues["useFreeTextData"]>(false);
  const [updatedItemsToFreeTextData, setUpdatedItemsToFreeTextData] = useState<
    TradeFormValues["updatedItemsToFreeTextData"]
  >({
    [OrderTypes.ENTRY]: true,
    [OrderTypes.TAKE_PROFIT]: true,
    [OrderTypes.STOP]: true,
  });

  const [showRedistributeTargetsButton, setShowRedistributeTargetsButton] =
    useState<TradeFormValues["showRedistributeTargetsButton"]>({
      [OrderTypes.ENTRY]: false,
      [OrderTypes.TAKE_PROFIT]: false,
      [OrderTypes.STOP]: false,
    });
  const [
    shouldRedistributePriceTargets,
    setOrderToShouldRedistributePriceTargets,
  ] = useState<TradeFormValues["shouldRedistributePriceTargets"]>({
    [OrderTypes.ENTRY]: false,
    [OrderTypes.TAKE_PROFIT]: false,
    [OrderTypes.STOP]: false,
  });
  const [priceTargetsModified, setOrderToPriceTargetsModified] = useState<
    TradeFormValues["priceTargetsModified"]
  >({
    [OrderTypes.ENTRY]: false,
    [OrderTypes.TAKE_PROFIT]: false,
    [OrderTypes.STOP]: false,
  });
  const [strategyValuesModified, setOrderToStrategyValuesModified] = useState<
    TradeFormValues["strategyValuesModified"]
  >({
    [OrderTypes.ENTRY]: false,
    [OrderTypes.TAKE_PROFIT]: false,
    [OrderTypes.STOP]: false,
  });
  const [isStrategyInitialized, setIsStrategyInitialized] = useState<
    TradeFormValues["isStrategyInitialized"]
  >({
    [OrderTypes.ENTRY]: false,
    [OrderTypes.TAKE_PROFIT]: false,
    [OrderTypes.STOP]: false,
  });
  const [ignoreOrdersRatios, setIgnoreOrdersRatios] = useState<
    TradeFormValues["ignoreOrdersRatios"]
  >({
    [OrderTypes.ENTRY]: false,
    [OrderTypes.TAKE_PROFIT]: false,
    [OrderTypes.STOP]: false,
  });
  const [showNewCustomStrategy, setShowNewCustomStrategy] = useState<
    TradeFormValues["showNewCustomStrategy"]
  >({
    [OrderTypes.ENTRY]: false,
    [OrderTypes.TAKE_PROFIT]: false,
    [OrderTypes.STOP]: false,
  });
  const [ratioTargets, setOrderToRatioTargets] = useState<
    TradeFormValues["ratioTargets"]
  >({
    [OrderTypes.ENTRY]: undefined,
    [OrderTypes.TAKE_PROFIT]: undefined,
    [OrderTypes.STOP]: undefined,
  });
  const [priceRangeInitialized, setPriceRangeInitialized] = useState<
    TradeFormValues["priceRangeInitialized"]
  >({
    [OrderTypes.ENTRY]: false,
    [OrderTypes.TAKE_PROFIT]: false,
    [OrderTypes.STOP]: false,
  });
  const [shouldUpdatePriceRange, setShouldUpdatePriceRange] = useState<
    TradeFormValues["shouldUpdatePriceRange"]
  >({
    [OrderTypes.ENTRY]: false,
    [OrderTypes.TAKE_PROFIT]: false,
    [OrderTypes.STOP]: false,
  });
  const [shouldUpdateNumOfOrders, setShouldUpdateNumOfOrders] =
    useState<TradeFormValues["shouldUpdateNumOfOrders"]>(false);
  const [isTpNumOrdersInitialized, setIsTpNumOrdersInitialized] =
    useState<TradeFormValues["isTpNumOrdersInitialized"]>(false);
  const [strategyHasFixedNumOrders, setStrategyHasFixedNumOrders] = useState<
    TradeFormValues["strategyHasFixedNumOrders"]
  >({
    [OrderTypes.ENTRY]: false,
    [OrderTypes.TAKE_PROFIT]: false,
    [OrderTypes.STOP]: false,
  });
  const [showedOppositePositionError, setShowedOppositePositionError] =
    useState<TradeFormValues["showedOppositePositionError"]>(false);
  const [ordersInitialized, setOrdersInitialized] = useState<
    TradeFormValues["ordersInitialized"]
  >({
    [OrderTypes.ENTRY]: false,
    [OrderTypes.TAKE_PROFIT]: false,
    [OrderTypes.STOP]: false,
  });
  const [numOrdersChangedManually, setNumOrdersChangedManually] = useState<
    TradeFormValues["numOrdersChangedManually"]
  >({
    [OrderTypes.ENTRY]: false,
    [OrderTypes.TAKE_PROFIT]: false,
    [OrderTypes.STOP]: false,
  });
  const [stopDistanceInputModified, setStopDistanceInputModified] =
    useState<TradeFormValues["stopDistanceInputModified"]>(false);
  const [stopBaselineModified, setStopBaselineModified] =
    useState<TradeFormValues["stopBaselineModified"]>(false);
  const [valuesModified, setValuesModified] =
    useState<TradeFormValues["valuesModified"]>(false);

  const [prevGeneralOptionsMapping, setPrevGeneralOptionsMapping] =
    useState<TradeFormValues["prevGeneralOptionsMapping"]>();

  const formValues: TradeFormValues = {
    account,
    exchanges,
    subGroupIds,
    accountChanged,
    symbols,
    symbolChanged,
    amount,
    amountPercent,
    amountType,
    amountInitialized,
    quantity,
    quantityPercent,
    extraInfo,
    entryOrders,
    takeProfitOrders,
    stopOrders,
    defaultStop,
    existingQuantityOrder,
    useTakeProfitOrders,
    useStopOrders,
    trailingEntry,
    trailingTakeProfit,
    trailingStop,
    stopTimeout,
    useTrailing,
    tabWithErrors,
    rawFreeText,
    freeTextTradeData,
    freeTextProcessed,
    useFreeTextData,
    updatedItemsToFreeTextData,
    showRedistributeTargetsButton,
    shouldRedistributePriceTargets,
    priceTargetsModified,
    strategyValuesModified,
    isStrategyInitialized,
    ignoreOrdersRatios,
    showNewCustomStrategy,
    ratioTargets,
    priceRangeInitialized,
    generalDataProcessed,
    shouldUpdatePriceRange,
    shouldUpdateNumOfOrders,
    isTpNumOrdersInitialized,
    strategyHasFixedNumOrders,
    showedOppositePositionError,
    ordersInitialized,
    numOrdersChangedManually,
    stopDistanceInputModified,
    stopBaselineModified,
    valuesModified,
    prevGeneralOptionsMapping,
  };

  const formSetters: TradeFormSetters = {
    setAccount,
    setExchanges,
    setSubGroupIds,
    setAccountChanged,
    setSymbols,
    setSymbolChanged,
    setAmount,
    setAmountPercent,
    setAmountType,
    setAmountInitialized,
    setQuantity,
    setQuantityPercent,
    setExtraInfo,
    setEntryOrders,
    setTakeProfitOrders,
    setStopOrders,
    setDefaultStop,
    setExistingQuantityOrder,
    setUseTakeProfitOrders,
    setUseStopOrders,
    setTrailingEntry,
    setTrailingTakeProfit,
    setTrailingStop,
    setStopTimeout,
    setUseTrailing: (orderType, value) =>
      setStateByType(setUseTrailing, orderType, value),
    setRawFreeText,
    setFreeTextTradeData,
    setFreeTextProcessed,
    setUseFreeTextData,
    setUpdatedItemsToFreeTextData: (orderType, value) =>
      setStateByType(setUpdatedItemsToFreeTextData, orderType, value),
    setShowRedistributeTargetsButton: (orderType, value) =>
      setStateByType(setShowRedistributeTargetsButton, orderType, value),
    setShouldRedistributePriceTargets: (orderType, value) =>
      setStateByType(
        setOrderToShouldRedistributePriceTargets,
        orderType,
        value
      ),
    setPriceTargetsModified: (orderType, value) =>
      setStateByType(setOrderToPriceTargetsModified, orderType, value),
    setStrategyValuesModified: (orderType, value) =>
      setStateByType(setOrderToStrategyValuesModified, orderType, value),
    setIsStrategyInitialized: (orderType, value) =>
      setStateByType(setIsStrategyInitialized, orderType, value),
    setIgnoreOrdersRatios: (orderType, value) =>
      setStateByType(setIgnoreOrdersRatios, orderType, value),
    setShowNewCustomStrategy: (orderType, value) =>
      setStateByType(setShowNewCustomStrategy, orderType, value),
    setRatioTargets: (orderType, value) =>
      setStateByType(setOrderToRatioTargets, orderType, value),
    setPriceRangeInitialized: (orderType, value) =>
      setStateByType(setPriceRangeInitialized, orderType, value),
    setTabWithErrors: (tab, value) =>
      setStateByType(setTabWithErrors, tab, value),
    setGeneralDataProcessed,
    setShouldUpdatePriceRange: (orderType, value) =>
      setStateByType(setShouldUpdatePriceRange, orderType, value),
    setShouldUpdateNumOfOrders,
    setIsTpNumOrdersInitialized,
    setStrategyHasFixedNumOrders: (orderType, value) =>
      setStateByType(setStrategyHasFixedNumOrders, orderType, value),
    setShowedOppositePositionError,
    setOrdersInitialized: (orderType, value) =>
      setStateByType(setOrdersInitialized, orderType, value),
    setNumOrdersChangedManually: (orderType, value) =>
      setStateByType(setNumOrdersChangedManually, orderType, value),
    setStopDistanceInputModified,
    setStopBaselineModified,
    setValuesModified,
    setPrevGeneralOptionsMapping,
  };

  return {
    formValues,
    formSetters,
  };
};

export default useTradeFormValues;
